import { logger } from "./logger";
/**
 * Footer Data Cache Utility
 * Prevents duplicate API calls and rate limiting issues
 */
// import { logger } from "./logger";
interface CacheEntry<T> {
    data: T;
    timestamp: number;
    expiresAt: number;
}
class FooterCache {
    private cache = new Map<string, CacheEntry<any>>();
    private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
    private pendingRequests = new Map<string, Promise<any>>();
    private lastRequestTime = new Map<string, number>();
    private readonly MIN_REQUEST_INTERVAL = 1000; // 1 second minimum between requests
    /**
     * Get cached data if it exists and hasn't expired
     */
    get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        if (!entry)
            return null;
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            return null;
        }
        return entry.data;
    }
    /**
     * Set cached data with TTL
     */
    set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
        const now = Date.now();
        this.cache.set(key, {
            data,
            timestamp: now,
            expiresAt: now + ttl
        });
    }
    /**
     * Check if a request is already pending
     */
    hasPendingRequest(key: string): boolean {
        return this.pendingRequests.has(key);
    }
    /**
     * Set a pending request promise
     */
    setPendingRequest<T>(key: string, promise: Promise<T>): void {
        this.pendingRequests.set(key, promise);
    }
    /**
     * Get a pending request promise
     */
    getPendingRequest<T>(key: string): Promise<T> | null {
        return this.pendingRequests.get(key) || null;
    }
    /**
     * Clear a pending request
     */
    clearPendingRequest(key: string): void {
        this.pendingRequests.delete(key);
    }
    /**
     * Clear all cache entries
     */
    clear(): void {
        this.cache.clear();
        this.pendingRequests.clear();
    }
    /**
     * Clear cache entries matching a URL pattern
     */
    clearByPattern(pattern: string | RegExp): void {
        const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
        for (const key of this.cache.keys()) {
            if (regex.test(key)) {
                this.cache.delete(key);
            }
        }
        for (const key of this.pendingRequests.keys()) {
            if (regex.test(key)) {
                this.pendingRequests.delete(key);
            }
        }
    }
    /**
     * Get cache statistics
     */
    getStats(): {
        size: number;
        pendingRequests: number;
    } {
        return {
            size: this.cache.size,
            pendingRequests: this.pendingRequests.size
        };
    }
    /**
     * Get last request time for rate limiting
     */
    getLastRequestTime(key: string): number | undefined {
        return this.lastRequestTime.get(key);
    }
    /**
     * Set last request time for rate limiting
     */
    setLastRequestTime(key: string, time: number): void {
        this.lastRequestTime.set(key, time);
    }
    /**
     * Get minimum request interval
     */
    getMinRequestInterval(): number {
        return this.MIN_REQUEST_INTERVAL;
    }
}
// Export singleton instance
export const footerCache = new FooterCache();
/**
 * Cached fetch function for footer data
 */
export async function cachedFetch<T>(url: string, options: RequestInit = {}, ttl: number = 5 * 60 * 1000, // 5 minutes default
extra?: {
    softTimeoutMs?: number;
    onSoftTimeout?: () => void;
}): Promise<T> {
    const cacheKey = `fetch:${url}:${JSON.stringify(options)}`;
    const softTimeoutMs = extra?.softTimeoutMs;
    const onSoftTimeout = extra?.onSoftTimeout;
    let softTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let softTimeoutTriggered = false;
    // Check cache first
    const cached = footerCache.get<T>(cacheKey);
    if (cached) {
        logger.debug('📦 Footer cache hit for:', { "arg0": url });
        return cached;
    }
    // Check if request is already pending
    if (footerCache.hasPendingRequest(cacheKey)) {
        logger.api('⏳ Footer request already pending for:', url);
        const pending = footerCache.getPendingRequest<T>(cacheKey)!;
        if (softTimeoutMs && softTimeoutMs > 0 && typeof onSoftTimeout === 'function') {
            const timeoutId = setTimeout(() => {
                onSoftTimeout();
            }, softTimeoutMs);
            return pending.finally(() => clearTimeout(timeoutId));
        }
        return pending;
    }
    // Rate limiting check
    const lastRequest = footerCache.getLastRequestTime(cacheKey);
    const now = Date.now();
    if (lastRequest && (now - lastRequest) < footerCache.getMinRequestInterval()) {
        logger.api('🚫 Footer rate limit: too soon since last request for:', url);
        // Return cached data if available, otherwise wait
        const cachedData = footerCache.get<T>(cacheKey);
        if (cachedData) {
            return cachedData;
        }
        // Wait a bit and try again
        await new Promise(resolve => setTimeout(resolve, footerCache.getMinRequestInterval() - (now - lastRequest)));
    }
    // Make the request
    logger.api('🌐 Footer cache miss, making request to:', url);
    // Track request time for rate limiting
    footerCache.setLastRequestTime(cacheKey, now);
    if (softTimeoutMs && softTimeoutMs > 0 && typeof onSoftTimeout === 'function') {
        softTimeoutId = setTimeout(() => {
            softTimeoutTriggered = true;
            try {
                onSoftTimeout();
            }
            catch (timeoutError) {
                logger.warn('Footer cache soft timeout handler failed:', { "arg0": timeoutError });
            }
        }, softTimeoutMs);
    }
    const requestPromise = fetch(url, options)
        .then(async (response) => {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
    })
        .then((data) => {
        // Cache successful response
        footerCache.set(cacheKey, data, ttl);
        return data;
    })
        .finally(() => {
        if (softTimeoutId) {
            clearTimeout(softTimeoutId);
        }
        if (softTimeoutTriggered) {
            logger.debug('Footer cache soft timeout resolved after data arrived:', { "arg0": url });
        }
        // Clear pending request
        footerCache.clearPendingRequest(cacheKey);
    });
    // Store pending request
    footerCache.setPendingRequest(cacheKey, requestPromise);
    return requestPromise;
}
